import { USER_PERMISSIONS } from "@hyro/dashboard-permissions";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";

import CodelessIcon from "src/components/widgets/assistant-creation/CodelessIcon";
import DemoIcon from "src/components/widgets/assistant-creation/DemoIcon";
import LegacyIcon from "src/components/widgets/assistant-creation/LegacyIcon";
import NewDeploymentIcon from "src/components/widgets/assistant-creation/NewDeploymentIcon";
import PhoneIcon from "src/components/widgets/assistant-creation/PhoneIcon";
import ScreenIcon from "src/components/widgets/assistant-creation/ScreenIcon";
import SmsIcon from "src/components/widgets/assistant-creation/SmsIcon";

export const CACHE_DURATION = {
  ONE_MINUTE: 60 * 1000,
};

export const CONFIGURATION_VERSIONS = {
  DRAFT: "draft",
  ACTIVE: "active"
};

export const FRONTEGG_DASHBOARD_CLIENT_ID = import.meta.env.VITE_FRONTEGG_DASHBOARD_CLIENT_ID;
export const FRONTEGG_DASHBOARD_BASE_URL = import.meta.env.VITE_FRONTEGG_DASHBOARD_BASE_URL;

export const CONFIGURATIONS_DIRECTORY_NAME = "data";
export const ADMIN = "admin";
export const CONVERSATIONS = "conversations";
export const HOME = "home";
export const SENTIMENT_ANALYSIS = "sentiment-analysis";
export const VOICE_INSIGHTS = "voice";
export const CHAT_INSIGHTS = "chat";
export const AGENT_PERFORMANCE = "agent-performance";
export const INSIGHTS = "insights";
export const BACK_OFFICE = "back-office";
export const KNOWLEDGE_DEBUGGER = "knowledge-debugger";
export const KNOWLEDGE_EXPLORER = "knowledge-explorer";
export const SYSTEM_OVERRIDES = "system-overrides";
export const GENERAL_SETTINGS = "general";
export const CHANNEL_SETTINGS = "channel-settings";
export const SKILLS = "skills";
export const CAMPAIGN_MANAGER = "campaign-manager";
export const DATA_PROCESSING = "data-processing";
export const BUILDING_BLOCKS = "building-blocks";
export const INTEGRATIONS = "integrations";
export const KNOWLEDGE_SOURCES = "knowledge-studio";
export const AUDIT_LOG = "audit-log";
export const BRANCH_DEPLOYER = "branch-deployer";
export const CORE_HYRO_KPIS = "core-hyro-kpis";
export const TEMPLATE_SETTINGS = "template";
export const ALL_ASSISTANTS = "all-agents";
export const VOICE_OVERVIEW = "voice-overview";
export const ACCOUNT_MANAGEMENT = "account-management";
export const ACCOUNT_SETTINGS = "account-settings";
export const CLINIC_AVAILABILITY = "clinic-availability";
export const AGENT_SETTINGS = "agent-settings";
export const TRIGGER = "trigger";
export const SYNONYM = "synonym";
export const DRAFTS = "drafts";
export const FAILURES = "failures";
export const PIPELINE_PERFORMANCE = "pipeline-performance";
export const EXTERNAL_PREVIEW = "external-preview";
export const CLOUD_CACHE_UPDATE = "cloud-cache-update";
export const DATA_MAPPING = "data-mapping";
export const CONFIGURATION_EDITOR = "configuration-editor";
export const FEATURE_ADOPTION = "feature-adoption";
export const DATA_AGENT = "data-agent";
export const FEATURE_MANAGEMENT = "feature-management";
export const ALL_SUBSCRIPTIONS = "all-subscriptions";

export const hiddenTitlePages = [HOME, SENTIMENT_ANALYSIS, CLOUD_CACHE_UPDATE, DATA_MAPPING, CONFIGURATION_EDITOR, CONVERSATIONS, AGENT_SETTINGS, CLINIC_AVAILABILITY, DATA_PROCESSING, FEATURE_MANAGEMENT];

export const LINKS = {
  HYRO_SUPPORT: "https://hyro-ai.atlassian.net/servicedesk/customer/portals",
  HYRO_DOCS: "https://docs.hyro.ai"
};

export const ROLES = {
  EXTERNAL: "external"
};

export const OPERATIONS = {
  ADD: "Add",
  DELETE: "Delete",
  UPDATE: "Update"
};

export const SEVERITY_VARIANTS = {
  INFO: "info",
  ERROR: "error",
  SUCCESS: "success",
  WARNING: "warning"
};

export const COLOR_VARIANTS = {
  PRIMARY: "primary",
  SECONDARY: "secondary",
  ...SEVERITY_VARIANTS
};

export const DOWNLOAD_BTN_VARIANTS = {
  TEXT: "text",
  OUTLINED: "outlined"
};

export const DOWNLOAD_BTN_SIZES = {
  LARGE: "large",
  MEDIUM: "medium"
};

const COLLECTION_URL_TO_API_NAME = {
  "knowledge-studio": "knowledge-sources"
};

export const getApiCollectionName = (collectionName) =>
  COLLECTION_URL_TO_API_NAME[collectionName] || collectionName;

// A spec's collection_id prefix is a third naming scheme, distinct from both the url name
// ("knowledge-studio") and the api name ("knowledge-sources"). Prefixes are matched exactly
// against nlu's CollectionType enum. "knowledge" (legacy in-skill tables) is intentionally
// not mapped — it is a different collection type and must not create KS instances.
const COLLECTION_REF_TO_URL_NAME = {
  "knowledge-sources": KNOWLEDGE_SOURCES
};

export const getUrlCollectionName = (refCollectionName) =>
  COLLECTION_REF_TO_URL_NAME[refCollectionName] || refCollectionName;

export const COLLECTION_PAGES = [
  SKILLS,
  BUILDING_BLOCKS,
  INTEGRATIONS,
  KNOWLEDGE_SOURCES
];

export const PAGE_PERMISSIONS = {
  [SYSTEM_OVERRIDES]: USER_PERMISSIONS.SETTINGS_PAGE,
  [SKILLS]: USER_PERMISSIONS.SKILLS_PAGE,
  [BUILDING_BLOCKS]: USER_PERMISSIONS.BUILDING_BLOCKS_PAGE,
  [INTEGRATIONS]: USER_PERMISSIONS.INTEGRATIONS_PAGE,
  [KNOWLEDGE_SOURCES]: USER_PERMISSIONS.KNOWLEDGE_SOURCES_PAGE
};

export const NOTIFICATION_TYPES = {
  DEPLOYMENT: "Deployment",
  BUILD: "Build",
  PREVIEW: "Preview"
};

export const NOTIFICATIONS_STATES = {
  IN_PROGRESS: "In progress",
  PENDING_DEPLOYMENT: "Pending deployment",
  LIVE: "Live",
  IN_PROGRESS_FAILED: "In progress failed",
  PENDING_DEPLOYMENT_FAILED: "Pending deployment failed",
  COMPLETED: "Completed"
};

export const DRAFT_STATES = {
  READ_ONLY: "read-only",
  EDIT: "edit",
  OPEN: "open"
};

export const INSIGHTS_DASHBOARD_NAMES = {
  healthcare: "healthcare",
  overview: "overview",
  rAndD: "rnd",
  coreHyroKpis: "coreHyroKpis",
  voice: "voice",
  chat: "chat",
  agentPerformance: "agentPerformance"
};

export const INPUT_TYPES = {
  COLOR: "color",
  ADD_ONE: "add_one",
  PROMPT_BUTTON: "button",
  DESCRIPTION: "description",
  DROPZONE: "dropzone",
  DROPDOWN: "dropdown",
  BOOLEAN: "boolean",
  COMMA_SEPARATED_ARRAY: "comma_separated_array",
  MULTIPLE_CHOICE: "multiple_choice",
  TEXT: "text",
  TEXT_VERIFY: "text_verify"
};

export const DATA_SOURCE_TYPES = {
  EXTERNAL_FILE: "External File",
  API: "API",
  MANUAL: "Manual"
};

export const ASSISTANT_TYPES = {
  NEW_DEPLOYMENT: "New Deployment",
  LIVE: "Live",
  DEMO: "Demo",
  TEST: "Test",
  NO_LONGER_ACTIVE: "No Longer Active"
};

export const ASSISTANT_ENVIRONMENT = {
  STAGING: "staging",
  PRODUCTION: "production",
  DEMO: "demo",
  TEST: "test",
  DISABLED: "disabled"
};

export const ASSISTANT_CHANNELS = {
  VOICE: "Voice",
  PHONE: "Phone",
  WEB: "Web",
  SMS: "SMS"
};

export const REGIONS = {
  US: "US"
};

export const CHANNEL_TO_DISPLAY_NAME = { Phone: "Voice", Web: "Chat", Voice: "Voice", SMS: "SMS" };

export const CHANNEL_NAMES = [...new Set(Object.values(CHANNEL_TO_DISPLAY_NAME))];

export const ASSISTANT_METHODS = { CODELESS: "Codeless", LEGACY: "Legacy" };

export const ASSISTANT_ENIVRONMENT_UI_DATA = {
  [ASSISTANT_ENVIRONMENT.STAGING]: {
    backgroundColorPath: ["info", "200"],
    textColorPath: ["info", "600"],
    displayName: "Staging"
  },
  [ASSISTANT_ENVIRONMENT.PRODUCTION]: {
    backgroundColorPath: ["success", "200"],
    textColorPath: ["success", "700"],
    displayName: "Production"
  },
  [ASSISTANT_ENVIRONMENT.DEMO]: {
    backgroundColorPath: ["warning", "200"],
    textColorPath: ["warning", "700"],
    displayName: "Demo"
  },
  [ASSISTANT_ENVIRONMENT.TEST]: {
    backgroundColorPath: ["grey", "200"],
    textColorPath: ["text", "secondary"],
    displayName: "Test"
  },
  [ASSISTANT_ENVIRONMENT.DISABLED]: {
    backgroundColorPath: ["grey", "200"],
    textColorPath: ["text", "secondary"],
    displayName: "Disabled"
  }
};

export const VERTICALS = {
  HEALTHCARE: "Healthcare",
  REAL_ESTATE: "Real Estate",
  OTHER: "Other"
};

export const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const CONCEPT_TYPE = "concept/type";

export const WIDGET_TOKEN = "hyro.token";

export const BUILD_STATUSES = {
  LIVE: "Live",
  PENDING: "Pending",
  EXPIRED: "Expired",
  TERMINATED: "Terminated",
  FAILED: "Failed",
  NOT_FOUND: "Not Found",
  KG_UPDATE: "KG Update",
  CHANGED: "Changed"
};

export const PIPELINE_STATUSES = {
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  FAILED: "FAILED"
};

export const LANGUAGES = {
  ENGLISH: "ENGLISH",
  SPANISH: "SPANISH"
};

export const MAX_ASSISTANT_NAME_LENGTH = 34;

export const TOOLTIP_DELAY = {
  NONE: 0,
  SHORT: 100,
  LONG: 500
};

export const HYRO_ADMIN_PANEL_ACCOUNT_ID = "hyro-admin-panel";

export const ENVIRONMENTS = {
  LOCAL: "local",
  DEVELOPMENT: "development",
  BRANCH: "branch",
  PRODUCTION: "production"
};

export const TWILIO_ENVIRONMENTS = {
  DEV: "dev",
  UAT: "uat",
  PRODUCTION: "production"
};

export const TELEPHONY_TYPES = {
  DID: "DID",
  SIP: "SIP"
};

export const ENGAGEMENT_TYPES = {
  INBOUND: "INBOUND",
  OUTBOUND: "OUTBOUND"
};

export const TELEPHONY_LABELS = {
  [TELEPHONY_TYPES.DID]: "Direct Number",
  [TELEPHONY_TYPES.SIP]: "SIP"
};

export const BRANCH_NAMES = {
  MASTER: "master"
};

export const HOME_INSIGHTS = "Home Insights";

export const NAME_TO_ICON = {
  [ASSISTANT_CHANNELS.WEB]: ScreenIcon,
  [ASSISTANT_CHANNELS.VOICE]: PhoneIcon,
  [ASSISTANT_CHANNELS.PHONE]: PhoneIcon,
  [ASSISTANT_CHANNELS.SMS]: SmsIcon,
  [ASSISTANT_TYPES.NEW_DEPLOYMENT]: NewDeploymentIcon,
  [ASSISTANT_TYPES.DEMO]: DemoIcon,
  [VERTICALS.HEALTHCARE]: ContentPasteOutlinedIcon,
  [VERTICALS.REAL_ESTATE]: HomeWorkOutlinedIcon,
  [VERTICALS.OTHER]: CardGiftcardOutlinedIcon,
  [ASSISTANT_METHODS.CODELESS]: CodelessIcon,
  [ASSISTANT_METHODS.LEGACY]: LegacyIcon
};

export const TIMEZONE_KEYS = {
  EASTERN: "eastern",
  CENTRAL: "central",
  MOUNTAIN: "mountain",
  PACIFIC: "pacific",
  ALASKA: "alaska",
  HAWAII: "hawaii",
};

export const TIMEZONE_DISPLAY_NAMES = {
  [TIMEZONE_KEYS.EASTERN]: {
    short: "EDT",
    long: "Eastern Daylight Time",
    timezone: "America/New_York"
  },
  [TIMEZONE_KEYS.CENTRAL]: {
    short: "CDT",
    long: "Central Daylight Time",
    timezone: "America/Chicago"
  },
  [TIMEZONE_KEYS.MOUNTAIN]: {
    short: "MDT",
    long: "Mountain Daylight Time",
    timezone: "America/Denver"
  },
  [TIMEZONE_KEYS.PACIFIC]: {
    short: "PDT",
    long: "Pacific Daylight Time",
    timezone: "America/Los_Angeles"
  },
  [TIMEZONE_KEYS.ALASKA]: {
    short: "AKDT",
    long: "Alaska Daylight Time",
    timezone: "America/Anchorage"
  },
  [TIMEZONE_KEYS.HAWAII]: {
    short: "HST",
    long: "Hawaii–Aleutian Standard Time",
    timezone: "Pacific/Honolulu"
  }
};

export const DEFAULT_TIMEZONE = TIMEZONE_KEYS.EASTERN;

export const TIMEZONE_OPTIONS = [
  { key: TIMEZONE_KEYS.EASTERN, display_key: "Eastern Time (UTC-05:00)" },
  { key: TIMEZONE_KEYS.CENTRAL, display_key: "Central Time (UTC-06:00)" },
  { key: TIMEZONE_KEYS.MOUNTAIN, display_key: "Mountain Time (UTC-07:00)" },
  { key: TIMEZONE_KEYS.PACIFIC, display_key: "Pacific Time (UTC-08:00)" },
  { key: TIMEZONE_KEYS.ALASKA, display_key: "Alaska Time (UTC-09:00)" },
  { key: TIMEZONE_KEYS.HAWAII, display_key: "Hawaii–Aleutian Time (UTC-10:00)" },
];
